/**
 * Authentication Conflict Detector
 * 
 * Detects and prevents authentication conflicts that might cause
 * unwanted sign-outs, particularly for suspended users.
 */

import { tabCoordinator } from "./tabCoordinator";

export interface AuthConflictInfo {
  conflictType: 'cross_tab_signout' | 'session_race_condition' | 'stale_session_cleanup';
  severity: 'low' | 'medium' | 'high';
  description: string;
  recommendation: string;
  tabInfo: {
    currentTab: string;
    totalTabs: number;
    shouldHandleAuth: boolean;
  };
}

class AuthConflictDetector {
  private suspiciousSignOutCount = 0;
  private lastSignOutTime = 0;
  private readonly SUSPICIOUS_SIGNOUT_THRESHOLD = 3;
  private readonly RAPID_SIGNOUT_WINDOW = 30000; // 30 seconds

  /**
   * Detect if a sign-out operation might be problematic
   */
  public detectConflictBeforeSignOut(reason?: string): AuthConflictInfo | null {
    const now = Date.now();
    const activeTabs = tabCoordinator.getActiveTabsInfo();
    const shouldHandleAuth = tabCoordinator.shouldHandleAuth();
    const isInJoinFlow = tabCoordinator.isInJoinFlowContext();

    // Check for rapid successive sign-outs
    if (now - this.lastSignOutTime < this.RAPID_SIGNOUT_WINDOW) {
      this.suspiciousSignOutCount++;
    } else {
      this.suspiciousSignOutCount = 1;
    }
    this.lastSignOutTime = now;

    const tabInfo = {
      currentTab: tabCoordinator.getCurrentTabId(),
      totalTabs: activeTabs.length,
      shouldHandleAuth
    };

    // Detect cross-tab sign-out conflicts
    if (activeTabs.length > 1 && !shouldHandleAuth) {
      return {
        conflictType: 'cross_tab_signout',
        severity: 'high',
        description: 'Sign-out attempt from inactive tab while other tabs are active',
        recommendation: 'Prevent sign-out to avoid cross-tab conflicts',
        tabInfo
      };
    }

    // Detect suspicious rapid sign-outs
    if (this.suspiciousSignOutCount >= this.SUSPICIOUS_SIGNOUT_THRESHOLD) {
      return {
        conflictType: 'session_race_condition',
        severity: 'medium',
        description: `Rapid successive sign-outs detected (${this.suspiciousSignOutCount} in ${this.RAPID_SIGNOUT_WINDOW/1000}s)`,
        recommendation: 'Monitor for authentication race conditions',
        tabInfo
      };
    }

    // Detect join flow conflicts
    if (isInJoinFlow && activeTabs.length > 1 && reason?.includes('mismatch')) {
      return {
        conflictType: 'cross_tab_signout',
        severity: 'medium',
        description: 'Email mismatch sign-out in join flow with multiple tabs',
        recommendation: 'Coordinate sign-out across tabs to prevent conflicts',
        tabInfo
      };
    }

    return null;
  }

  /**
   * Log authentication conflict events
   */
  public logConflict(conflict: AuthConflictInfo, action: 'prevented' | 'allowed' | 'detected') {
    const logLevel = conflict.severity === 'high' ? 'error' : 
                    conflict.severity === 'medium' ? 'warn' : 'info';

    const message = `[AuthConflictDetector] ${action.toUpperCase()} ${conflict.conflictType}`;
    
    console[logLevel](message, {
      severity: conflict.severity,
      description: conflict.description,
      recommendation: conflict.recommendation,
      tabInfo: conflict.tabInfo,
      timestamp: new Date().toISOString(),
      currentPath: window.location.pathname
    });

    // Store conflict info for debugging
    try {
      const conflicts = JSON.parse(localStorage.getItem('auth_conflicts') || '[]');
      conflicts.push({
        ...conflict,
        action,
        timestamp: new Date().toISOString(),
        currentPath: window.location.pathname
      });
      
      // Keep only last 50 conflicts
      if (conflicts.length > 50) {
        conflicts.splice(0, conflicts.length - 50);
      }
      
      localStorage.setItem('auth_conflicts', JSON.stringify(conflicts));
    } catch (e) {
      console.warn('[AuthConflictDetector] Failed to store conflict info:', e);
    }
  }

  /**
   * Get recent conflict history for debugging
   */
  public getConflictHistory(): any[] {
    try {
      return JSON.parse(localStorage.getItem('auth_conflicts') || '[]');
    } catch (e) {
      return [];
    }
  }

  /**
   * Clear conflict history
   */
  public clearConflictHistory() {
    try {
      localStorage.removeItem('auth_conflicts');
      this.suspiciousSignOutCount = 0;
    } catch (e) {
      console.warn('[AuthConflictDetector] Failed to clear conflict history:', e);
    }
  }

  /**
   * Check if the current context is safe for auth operations
   */
  public isAuthContextSafe(): boolean {
    const activeTabs = tabCoordinator.getActiveTabsInfo();
    const shouldHandleAuth = tabCoordinator.shouldHandleAuth();
    
    // Safe if this tab should handle auth or there's only one tab
    return shouldHandleAuth || activeTabs.length <= 1;
  }
}

export const authConflictDetector = new AuthConflictDetector();
